import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { UserRound } from 'lucide-react'
import { shopBrowseCategories } from '#bv/data/products'
import { getFeaturedPanelProductIds, resolveFeaturedDeals } from '#bv/lib/featuredPanel'
import { getMergedCatalogFn, productsShownMerged } from '#bv/lib/productCatalog'
import { ProductCard } from '#bv/components/ProductCard'
import { CategoryIcon } from '#bv/components/CategoryIcon'
import { DiscordIcon } from '#bv/components/DiscordIcon'
import { FeatureIcon, type FeatureIconName } from '#bv/components/FeatureIcons'
import { newUiEnabled } from '#bv/newui/newUiFlag'
import { vouchesChannelText } from '#bv/lib/replacementPolicy'

export const Route = createFileRoute('/')({
  staleTime: 0,
  loader: async () => ({
    featuredIds: await getFeaturedPanelProductIds(),
    catalog: await getMergedCatalogFn(),
  }),
  component: HomePage,
})

/* ───────── DATA ───────── */

const faqs = [
  { q: 'How fast is delivery?', a: 'Most digital products are delivered instantly or within 5–15 minutes of payment confirmation. Times can vary by time zone, product type, and manual review. Check each product page for exact delivery windows.' },
  { q: 'Is checkout secure?', a: 'All payments are processed through MoneyMotion. Your card data never touches our servers.' },
  { q: 'What if something goes wrong?', a: `After delivery, vouch in ${vouchesChannelText()} within 24 hours if the product worked when you tested it — that activates your warranty. If something breaks inside that window, open a Discord ticket with your order ID. Dead on arrival? Reply in your ticket before vouching.` },
  { q: 'Do I need a Discord account to buy?', a: 'No — you can buy directly through the website. Discord is optional but recommended for faster support and community deals.' },
  { q: 'Are your accounts and products safe to use?', a: 'We take every precaution to ensure quality, but results can vary depending on the product type and how it\'s used. Every listing includes specific safety notes and usage guidelines where relevant.' },
]

const HERO_WORDS = ['Streaming.', 'Gaming.', 'Software.', 'Discord.', 'Social.']

/** New UI hero — full phrases, rotated on the headline gradient line. */
const NV_HERO_PHRASES = [
  'premium streaming access',
  'premium gaming accounts',
  'Discord Nitro & boosts',
  'VPN & software keys',
  'social growth services',
] as const

const NV_WHY_PILLARS = [
  { icon: 'lock' as FeatureIconName, title: 'Verified checkout', desc: 'Payments processed by MoneyMotion with SSL encryption.', color: '#60a5fa' },
  { icon: 'shield' as FeatureIconName, title: 'Clear policies', desc: 'Vouch-activated warranty and eligibility rules are spelled out before you buy.', color: '#a78bfa' },
  { icon: 'message' as FeatureIconName, title: 'Reachable support', desc: 'Discord-first support with guided resolution when something needs attention.', color: '#a855f7' },
  { icon: 'check' as FeatureIconName, title: 'Curated listings', desc: 'A tighter Vault — fewer listings, higher confidence in what you pick.', color: '#34d399' },
] as const

const SOCIAL_PROOF = [
  'Got my Netflix in under 2 minutes, insane',
  'Best Nitro prices I\u2019ve ever seen',
  'Support replaced my account within an hour',
  'Bought 5 products so far, all flawless',
  'Spotify shared access is actually legit',
  'ExpressVPN key worked first try',
  'Prices are unbeatable fr',
  'Steam game delivered before I could blink',
]

/* ───────── HOOKS ───────── */

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect() } }, { threshold })
    obs.observe(el)
    return () => obs.disconnect()
  }, [threshold])
  return { ref, visible }
}

function useRotatingWord(words: string[], intervalMs = 2800) {
  const [idx, setIdx] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % words.length), intervalMs)
    return () => clearInterval(t)
  }, [words.length, intervalMs])
  return words[idx]
}

function HeroRotatingPhrase({
  phrases,
  intervalMs = 3600,
  className = '',
}: {
  phrases: readonly string[]
  intervalMs?: number
  className?: string
}) {
  const [idx, setIdx] = useState(0)
  const [prevIdx, setPrevIdx] = useState<number | null>(null)

  useEffect(() => {
    if (phrases.length < 2) return
    const t = setInterval(() => {
      setIdx((current) => {
        setPrevIdx(current)
        return (current + 1) % phrases.length
      })
    }, intervalMs)
    return () => clearInterval(t)
  }, [phrases.length, intervalMs])

  useEffect(() => {
    if (prevIdx === null) return
    const done = window.setTimeout(() => setPrevIdx(null), 680)
    return () => window.clearTimeout(done)
  }, [prevIdx, idx])

  const current = phrases[idx] ?? phrases[0]
  const previous = prevIdx !== null ? phrases[prevIdx] : null

  return (
    <span className={`nv-hero3-accent-line ${className}`.trim()} aria-live="polite">
      <span className="nv-hero3-accent-stage">
        {previous ? (
          <span className="nv-hero3-accent nv-hero3-accent--out">{previous}.</span>
        ) : null}
        <span key={idx} className="nv-hero3-accent nv-hero3-accent--in">
          {current}.
        </span>
      </span>
    </span>
  )
}

/* ───────── SMALL COMPONENTS ───────── */

function Anim({ children, className, delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, visible } = useInView()
  return (
    <div ref={ref} className={className} style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)', transition: `opacity 0.7s cubic-bezier(.16,1,.3,1) ${delay}s, transform 0.7s cubic-bezier(.16,1,.3,1) ${delay}s` }}>
      {children}
    </div>
  )
}

function AnimatedCounter({ target, suffix = '', prefix = '' }: { target: number; suffix?: string; prefix?: string }) {
  const { ref, visible } = useInView(0.3)
  const [val, setVal] = useState(0)
  useEffect(() => {
    if (!visible) return
    let frame: number
    const start = performance.now()
    const dur = 1400
    const tick = (now: number) => {
      const p = Math.min((now - start) / dur, 1)
      const ease = 1 - Math.pow(1 - p, 3)
      setVal(Math.round(ease * target))
      if (p < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [visible, target])
  return <span ref={ref}>{prefix}{val.toLocaleString()}{suffix}</span>
}

function Orb({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <div className={`absolute rounded-full pointer-events-none ${className ?? ''}`} style={{ filter: 'blur(80px)', ...style }} aria-hidden />
}

function SectionWash({ color = '#00bfff', flip = false }: { color?: string; flip?: boolean }) {
  return (
    <div className="relative h-24 sm:h-32 overflow-hidden pointer-events-none" aria-hidden style={{ transform: flip ? 'scaleY(-1)' : undefined }}>
      <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, transparent 0%, ${color}06 50%, transparent 100%)` }} />
      <div className="absolute left-1/2 -translate-x-1/2 bottom-0 w-24 h-px" style={{ background: `linear-gradient(90deg, transparent, ${color}40, transparent)` }} />
    </div>
  )
}

function StarField() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    let animId: number
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()
    window.addEventListener('resize', resize)
    const stars = Array.from({ length: 80 }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: Math.random() * 1.2 + 0.3,
      phase: Math.random() * Math.PI * 2,
      speed: Math.random() * 0.4 + 0.2,
    }))
    const draw = (t: number) => {
      const w = canvas.width / dpr
      const h = canvas.height / dpr
      ctx.clearRect(0, 0, w, h)
      for (const s of stars) {
        const alpha = 0.15 + 0.35 * (0.5 + 0.5 * Math.sin(t * 0.001 * s.speed + s.phase))
        ctx.beginPath()
        ctx.arc(s.x * w, s.y * h, s.r, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(255,180,230,${alpha})`
        ctx.fill()
      }
      animId = requestAnimationFrame(draw)
    }
    animId = requestAnimationFrame(draw)
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize) }
  }, [])
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden />
}

function MouseSpotlight() {
  const ref = useRef<HTMLDivElement>(null)
  const pos = useRef({ x: 50, y: 50 })
  const onMove = useCallback((e: React.MouseEvent) => {
    const r = ref.current?.getBoundingClientRect()
    if (!r) return
    pos.current = { x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 }
    if (ref.current) ref.current.style.background = `radial-gradient(600px circle at ${pos.current.x}% ${pos.current.y}%, rgba(0, 191, 255,0.06), transparent 60%)`
  }, [])
  return <div ref={ref} className="absolute inset-0 pointer-events-none transition-opacity duration-500" onMouseMove={onMove} style={{ pointerEvents: 'auto', zIndex: 1, background: 'transparent' }} />
}

function SocialProofTicker() {
  return (
    <div className="relative overflow-hidden py-6" style={{ maskImage: 'linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)' }}>
      <div className="marquee-inner flex items-center gap-6 whitespace-nowrap" style={{ width: 'max-content' }}>
        {[...Array(2)].map((_, setIdx) => (
          <span key={setIdx} className="flex items-center gap-6">
            {SOCIAL_PROOF.map((text, i) => (
              <span
                key={`${setIdx}-${i}`}
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium"
                style={{
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.22))',
                  border: '1px solid rgba(255,255,255,0.07)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
                  color: '#c9aac4',
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="#93c5fd" stroke="none">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
                {text}
              </span>
            ))}
          </span>
        ))}
      </div>
    </div>
  )
}

function FAQItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false)
  const bodyRef = useRef<HTMLDivElement>(null)
  return (
    <div
      className="group rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer"
      style={{
        background: open ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.015)',
        border: `1px solid ${open ? 'rgba(0, 191, 255,0.2)' : 'rgba(0,191,255,0.06)'}`,
        boxShadow: open ? '0 8px 32px rgba(0, 191, 255,0.06)' : 'none',
      }}
      onClick={() => setOpen(!open)}
    >
      <div className="flex items-center justify-between gap-4 px-6 py-5">
        <div className="flex items-center gap-4">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-xs font-bold tabular-nums" style={{ background: 'rgba(0, 191, 255,0.1)', color: '#93c5fd' }}>
            {String(index + 1).padStart(2, '0')}
          </span>
          <span className="font-medium text-sm sm:text-base" style={{ color: '#f0f6ff' }}>{q}</span>
        </div>
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-all duration-300" style={{ background: open ? 'rgba(0, 191, 255,0.2)' : 'rgba(0,191,255,0.06)', color: open ? '#00bfff' : '#8ecfff', transform: open ? 'rotate(45deg)' : 'none' }}>+</span>
      </div>
      <div ref={bodyRef} className="overflow-hidden transition-all duration-300" style={{ maxHeight: open ? `${(bodyRef.current?.scrollHeight ?? 200) + 20}px` : '0px', opacity: open ? 1 : 0 }}>
        <div className="px-6 pb-5 pl-[4.25rem]">
          <p className="text-sm leading-relaxed" style={{ color: '#8aa0c4' }}>{a}</p>
        </div>
      </div>
    </div>
  )
}

/* ───────── MAIN PAGE ───────── */

export default function HomePage() {
  const { featuredIds, catalog } = Route.useLoaderData()
  const shopGrid = productsShownMerged(catalog)
  const featuredProducts = resolveFeaturedDeals(shopGrid, featuredIds)
  const rotatingWord = useRotatingWord(HERO_WORDS)

  if (newUiEnabled()) {
    return <NewHomePage featuredProducts={featuredProducts} />
  }

  return (
    <div className="min-h-screen grid-bg">

      {/* ═══════════ HERO ═══════════ */}
      <section className="relative flex min-h-[82vh] items-center overflow-hidden sm:min-h-[94vh]">
        <StarField />
        <MouseSpotlight />

        <div className="absolute inset-0" style={{ background: 'var(--hero-wash-a)' }} />
        <div className="absolute inset-0" style={{ background: 'var(--hero-wash-b)' }} />

        <Orb className="bv-float" style={{ width: 350, height: 350, top: '8%', left: '58%', background: 'var(--hero-orb-a)' }} />
        <Orb className="bv-float-slow" style={{ width: 220, height: 220, bottom: '12%', left: '8%', background: 'var(--hero-orb-b)' }} />
        <Orb className="bv-float" style={{ width: 180, height: 180, top: '50%', right: '3%', background: 'var(--hero-orb-c)', animationDelay: '2s' }} />

        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
          <div className="absolute top-0 right-0 w-[60vw] h-[60vw] opacity-[0.025]" style={{ background: 'repeating-linear-gradient(45deg, rgba(0, 191, 255,0.35) 0px, rgba(0, 191, 255,0.35) 1px, transparent 1px, transparent 40px)' }} />
        </div>

        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
          <div className="max-w-4xl">
            <div
              className="bv-border-glow inline-flex items-center gap-2.5 rounded-full px-5 py-2.5 text-[0.7rem] font-semibold tracking-[0.22em] mb-8"
              style={{ background: 'rgba(0, 191, 255,0.06)', color: '#93c5fd', border: '1px solid rgba(0, 191, 255,0.18)' }}
            >
              <span className="pulse-dot h-2 w-2 rounded-full bg-[#00bfff] inline-block" />
              PREMIUM DIGITAL MARKETPLACE
            </div>

            <h1
              className="mb-4 font-display leading-none"
              style={{ fontSize: 'clamp(2.35rem, 9.5vw, 7.2rem)', lineHeight: 0.92, letterSpacing: '-0.035em' }}
            >
              <span className="block chrome-text">Pulse Shop</span>
              <span
                className="block"
                style={{
                  background: 'linear-gradient(135deg, #93c5fd 0%, #00bfff 38%, #a855f7 72%, #7b2fbe 100%)',
                  backgroundSize: '200% 200%',
                  animation: 'bv-gradient-shift 6s ease infinite',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                IS OPEN.
              </span>
            </h1>

            <p className="text-lg sm:text-xl leading-relaxed mb-3 max-w-xl" style={{ color: '#c9aac4' }}>
              Premium digital products — curated, fast, and priced below retail.
            </p>
            <div className="mb-10 flex items-center gap-2 text-lg sm:text-xl" style={{ color: '#5a6d8a' }}>
              <span className="h-5 w-px" style={{ background: 'rgba(0, 191, 255,0.4)' }} />
              <span className="font-display text-2xl sm:text-3xl tracking-wide" style={{ color: '#93c5fd', transition: 'opacity 0.3s' }} key={rotatingWord}>{rotatingWord}</span>
            </div>

            <div className="mb-14 grid grid-cols-3 gap-2 sm:mb-16 sm:flex sm:flex-wrap sm:items-center sm:gap-4">
              <Link
                to="/shop"
                className="btn-primary group inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl px-3 text-sm font-semibold sm:h-14 sm:w-auto sm:px-8 sm:text-base"
                style={{ boxShadow: '0 10px 32px rgba(0, 191, 255,0.22), 0 0 0 1px rgba(255,255,255,0.05) inset' }}
              >
                <span className="hidden sm:inline">Browse Pulse Shop</span><span className="sm:hidden">Browse</span>
                <svg className="transition-transform duration-300 group-hover:translate-x-1" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </Link>
              <a
                href="https://discord.gg/tGxhDfRkdE"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-discord h-12 w-full rounded-xl px-3 text-sm sm:h-14 sm:w-auto sm:px-8 sm:text-base"
              >
                <DiscordIcon size={16} white className="shrink-0" /><span className="hidden sm:inline">Join Discord</span><span className="sm:hidden">Discord</span>
              </a>
              <Link
                to="/customer"
                className="btn-chrome inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl px-3 text-sm font-semibold sm:h-14 sm:w-auto sm:px-8 sm:text-base"
                style={{ borderColor: 'rgba(255,255,255,0.08)', color: '#a8c4e8' }}
                title="Customer dashboard"
                aria-label="Customer dashboard"
              >
                <UserRound className="h-[18px] w-[18px]" strokeWidth={2.2} /><span className="hidden sm:inline">Dashboard</span><span className="sm:hidden">Account</span>
              </Link>
            </div>

            {/* Stat counters */}
            <div className="grid w-full max-w-4xl grid-cols-2 gap-3 md:grid-cols-4">
              {[
                { icon: 'lock' as FeatureIconName | null, value: 'MoneyMotion', label: 'Secure payments', counter: null },
                { icon: 'zap' as FeatureIconName | null, value: null, label: 'Avg delivery', counter: { n: 5, suffix: ' min', prefix: '<' } },
                { icon: 'shield' as FeatureIconName | null, value: null, label: 'Replacement', counter: { n: 24, suffix: 'h', prefix: '' } },
                { icon: null, value: 'Discord', label: 'Live support', counter: null },
              ].map((stat, i) => (
                <div key={i} className="group relative overflow-hidden rounded-2xl px-3 py-3 sm:px-4 sm:py-4 transition-all duration-300" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(0,191,255,0.08)' }}>
                  <div className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{ background: 'radial-gradient(ellipse at center, rgba(0, 191, 255,0.06), transparent 70%)' }} />
                  <div className="relative flex items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ background: 'rgba(0, 191, 255,0.1)', border: '1px solid rgba(0, 191, 255,0.15)', color: '#8ecfff' }}>
                      {stat.icon ? <FeatureIcon name={stat.icon} size={16} className="text-current" /> : <DiscordIcon size={16} white className="shrink-0" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      {stat.counter ? (
                        <p className="m-0 font-display text-lg font-bold leading-none tabular-nums tracking-tight text-[#93c5fd] sm:text-xl">
                          <AnimatedCounter target={stat.counter.n} prefix={stat.counter.prefix} suffix={stat.counter.suffix} />
                        </p>
                      ) : (
                        <p className="m-0 truncate text-sm font-semibold leading-none tracking-tight text-[#f0f6ff] sm:text-[0.9375rem]">
                          {stat.value}
                        </p>
                      )}
                      <p
                        className="m-0 mt-1 truncate text-[0.625rem] font-semibold uppercase leading-none tracking-[0.12em] sm:text-[10px]"
                        style={{ color: '#5a6d8a' }}
                      >
                        {stat.label}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bv-scroll-hint absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 z-10">
          <div className="text-[0.55rem] font-bold uppercase tracking-[0.25em]" style={{ color: '#5a6d8a' }}>Scroll</div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5a6d8a" strokeWidth="2"><path d="M12 5v14M5 12l7 7 7-7"/></svg>
        </div>
      </section>

      {/* Social proof ticker */}
      <SocialProofTicker />

      <SectionWash />

      {/* ═══════════ CATEGORIES ═══════════ */}
      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <Anim>
            <div className="mb-12">
              <div className="section-accent">
                <h2 className="font-display text-4xl sm:text-5xl tracking-tight chrome-text">CATEGORIES</h2>
              </div>
              <p className="mt-2 text-sm" style={{ color: '#5a6d8a' }}>Browse by product type — everything you need in one vault.</p>
            </div>
          </Anim>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {shopBrowseCategories.map((cat, i) => (
              <Anim key={cat.id} delay={i * 0.06}>
                <Link to="/shop" search={{ category: cat.id, q: undefined, sort: undefined }} className="glass glass-hover relative overflow-hidden rounded-2xl p-6 flex flex-col items-center text-center gap-4 group h-full">
                  <div className="absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100" style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(0, 191, 255,0.08), transparent 70%)' }} />
                  <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl transition-all duration-300 group-hover:scale-110 group-hover:shadow-[0_0_24px_rgba(0, 191, 255,0.25)]" style={{ background: 'linear-gradient(135deg, rgba(0, 191, 255,0.18), rgba(139,92,246,0.08))', border: '1px solid rgba(0, 191, 255,0.2)', color: '#00bfff' }}>
                    <CategoryIcon id={cat.id} />
                  </div>
                  <div className="relative">
                    <div className="font-semibold text-sm" style={{ color: '#f0f6ff' }}>{cat.label}</div>
                    <div className="text-xs mt-1 line-clamp-2" style={{ color: '#5a6d8a' }}>{cat.description}</div>
                  </div>
                </Link>
              </Anim>
            ))}
          </div>
        </div>
      </section>

      <SectionWash color="#8b5cf6" />

      {/* ═══════════ FEATURED DEALS ═══════════ */}
      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <Anim>
            <div className="mb-10 flex items-start justify-between gap-4 sm:items-end">
              <div>
                <div className="section-accent">
                  <div className="flex items-center gap-3">
                    <h2 className="font-display text-4xl sm:text-5xl tracking-tight chrome-text">FEATURED DEALS</h2>
                    <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[0.6rem] font-bold uppercase tracking-widest" style={{ background: 'rgba(0, 191, 255,0.12)', color: '#93c5fd', border: '1px solid rgba(0, 191, 255,0.2)' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="#93c5fd" stroke="none"><path d="M13.5 2c-.7 0-1.3.4-1.4 1L11 8H5c-.6 0-1.1.3-1.4.8s-.2 1.1.1 1.5l4.3 5.3L6.5 21c-.1.5.1 1 .6 1.3.4.3 1 .3 1.4 0l5-3.5 5 3.5c.2.1.4.2.6.2.3 0 .5-.1.8-.3.4-.3.7-.8.6-1.3L19 15.6l4.3-5.3c.3-.4.4-.9.1-1.4S22.6 8 22 8h-6l-1.1-5c-.1-.6-.7-1-1.4-1z"/></svg>
                      Curated
                    </span>
                  </div>
                </div>
                <p className="mt-2 text-sm" style={{ color: '#5a6d8a' }}>Top picks from Pulse Shop — staff-curated highlights.</p>
              </div>
              <Link to="/shop" className="hidden sm:flex items-center gap-2 text-sm font-medium transition-colors hover:text-white group" style={{ color: '#8ecfff' }}>
                View all<svg className="transition-transform duration-200 group-hover:translate-x-1" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </Link>
            </div>
          </Anim>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {featuredProducts.map((product, i) => (
              <Anim key={product.id} delay={i * 0.08}>
                <ProductCard product={product} variant="vault" />
              </Anim>
            ))}
          </div>

          <div className="mt-8 text-center sm:hidden">
            <Link to="/shop" className="btn-chrome inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm">View All Products</Link>
          </div>
        </div>
      </section>

      <SectionWash />

      {/* ═══════════ WHY PulseDigital — BENTO GRID ═══════════ */}
      <section className="relative py-20 px-4 sm:px-6 overflow-hidden">
        <Orb className="bv-float-slow" style={{ width: 280, height: 280, top: '15%', right: '3%', background: 'rgba(0, 191, 255,0.04)' }} />
        <div className="relative max-w-7xl mx-auto">
          <Anim>
            <div className="mb-14 text-center">
              <div className="inline-block section-accent text-left">
                <h2 className="font-display text-4xl sm:text-5xl tracking-tight chrome-text">WHY PulseDigital</h2>
              </div>
              <p className="mt-3 text-sm max-w-lg mx-auto" style={{ color: '#5a6d8a' }}>Fast delivery, secure payments, and support when you need it.</p>
            </div>
          </Anim>

          {/* Bento: 2 large cards (row 1), 4 cards (row 2) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {([
              { icon: 'zap' as FeatureIconName, title: 'Instant Delivery', desc: 'Most orders are fulfilled within 5 minutes of payment. Our automated systems run 24/7 — delivery times can vary by time zone and product.', color: '#fbbf24', span: 2 },
              { icon: 'lock' as FeatureIconName, title: 'Bank-Grade Security', desc: 'MoneyMotion checkout with SSL encryption. Your payment details never touch our servers.', color: '#60a5fa', span: 2 },
              { icon: 'check' as FeatureIconName, title: 'Community Feedback', desc: 'Buyers share feedback in our Discord. Real discussion, real vouches.', color: '#34d399', span: 1 },
              { icon: 'shield' as FeatureIconName, title: 'Vouch-activated warranty', desc: `Post in ${vouchesChannelText(false)} after delivery to start your window.`, color: '#a78bfa', span: 1 },
              { icon: 'message' as FeatureIconName, title: 'Live Discord Support', desc: 'Ticket response in minutes from trained staff.', color: '#a855f7', span: 1 },
              { icon: 'money' as FeatureIconName, title: 'Unbeatable Prices', desc: '60–80% below retail. Direct supplier pricing.', color: '#00bfff', span: 1 },
            ] as const).map((item, i) => (
              <Anim key={item.title} delay={i * 0.07} className={item.span === 2 ? 'lg:col-span-2' : ''}>
                <div className="glass group relative overflow-hidden rounded-2xl p-7 transition-all duration-300 hover:border-[rgba(0,191,255,0.18)] hover:shadow-[0_8px_40px_rgba(0, 191, 255,0.08)] h-full">
                  <span className="feature-num">{String(i + 1).padStart(2, '0')}</span>
                  <div className="absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100" style={{ background: `radial-gradient(ellipse at 30% 20%, ${item.color}08, transparent 60%)` }} />
                  <div className="relative">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl transition-transform duration-300 group-hover:scale-110" style={{ background: `${item.color}15`, border: `1px solid ${item.color}25`, color: item.color }}>
                      <FeatureIcon name={item.icon} size={24} strokeWidth={1.75} className="text-current" />
                    </div>
                    <h3 className="font-semibold text-base mb-2" style={{ color: '#f0f6ff' }}>{item.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: '#5a6d8a' }}>{item.desc}</p>
                  </div>
                </div>
              </Anim>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ TRUST STRIP ═══════════ */}
      <section className="py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <Anim>
            <div className="relative overflow-hidden rounded-2xl py-8 px-6 sm:px-10" style={{ background: 'linear-gradient(135deg, rgba(0, 191, 255,0.08) 0%, rgba(139,92,246,0.04) 50%, rgba(123,47,190,0.04) 100%)', border: '1px solid rgba(0, 191, 255,0.12)' }}>
              <div className="shimmer absolute inset-0 pointer-events-none opacity-40" />
              <div className="relative flex flex-wrap items-center justify-center gap-x-10 gap-y-4 sm:gap-x-14">
                {([
                  { icon: 'lockKey' as FeatureIconName, label: 'SSL Encrypted', color: '#60a5fa' },
                  { icon: 'card' as FeatureIconName, label: 'MoneyMotion', color: '#00bfff' },
                  { icon: 'zap' as FeatureIconName, label: 'Instant Delivery', color: '#fbbf24' },
                  { icon: 'shield' as FeatureIconName, label: 'Vouch to activate warranty', color: '#a78bfa' },
                  { icon: 'handshake' as FeatureIconName, label: 'Trusted', color: '#8ecfff' },
                  { icon: 'star' as FeatureIconName, label: 'Highly Rated', color: '#c4b5fd' },
                ]).map((badge) => (
                  <div key={badge.label} className="flex items-center gap-2.5">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: `${badge.color}14`, border: `1px solid ${badge.color}28`, color: badge.color }}>
                      <FeatureIcon name={badge.icon} size={18} className="shrink-0 text-current" />
                    </div>
                    <span className="text-sm font-medium" style={{ color: '#a8c4e8' }}>{badge.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </Anim>
        </div>
      </section>

      <SectionWash color="#8b5cf6" />

      {/* ═══════════ FULL VAULT ═══════════ */}
      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <Anim>
            <div className="mb-10 flex items-start justify-between gap-4 sm:items-end">
              <div>
                <div className="section-accent">
                  <div className="flex items-center gap-3">
                    <h2 className="font-display text-4xl sm:text-5xl tracking-tight chrome-text">THE FULL VAULT</h2>
                    <span className="rounded-full px-3 py-1 text-[0.6rem] font-bold tabular-nums" style={{ background: 'rgba(255,255,255,0.04)', color: '#5a6d8a', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <AnimatedCounter target={shopGrid.length} /> products
                    </span>
                  </div>
                </div>
                <p className="mt-2 text-sm" style={{ color: '#5a6d8a' }}>Every product, every category. Browse them all.</p>
              </div>
              <Link to="/shop" className="hidden sm:flex items-center gap-2 text-sm font-medium transition-colors hover:text-white group" style={{ color: '#8ecfff' }}>
                Full shop<svg className="transition-transform duration-200 group-hover:translate-x-1" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </Link>
            </div>
          </Anim>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {shopGrid.slice(0, 8).map((product, i) => (
              <Anim key={product.id} delay={i * 0.06}><ProductCard product={product} /></Anim>
            ))}
          </div>

          <Anim delay={0.3}>
            <div className="mt-12 text-center">
              <Link to="/shop" className="btn-primary group inline-flex w-full items-center justify-center gap-3 rounded-xl px-6 py-4 text-base font-semibold sm:w-auto sm:px-10">
                Enter The Full Vault
                <svg className="transition-transform duration-300 group-hover:translate-x-1" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </Link>
            </div>
          </Anim>
        </div>
      </section>

      <SectionWash />

      {/* ═══════════ FAQ ═══════════ */}
      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <Anim>
            <div className="mb-10 text-center">
              <div className="inline-block section-accent text-left">
                <h2 className="font-display text-4xl sm:text-5xl tracking-tight chrome-text">QUICK FAQ</h2>
              </div>
              <p className="mt-2 text-sm" style={{ color: '#5a6d8a' }}>Answers to the most common questions.</p>
            </div>
          </Anim>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <Anim key={faq.q} delay={i * 0.06}><FAQItem q={faq.q} a={faq.a} index={i} /></Anim>
            ))}
          </div>
          <Anim delay={0.3}>
            <div className="mt-8 text-center">
              <Link to="/faq" className="text-sm font-medium transition-colors hover:text-white group inline-flex items-center gap-1.5" style={{ color: '#8ecfff' }}>
                View all FAQs<svg className="transition-transform duration-200 group-hover:translate-x-1" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </Link>
            </div>
          </Anim>
        </div>
      </section>

      {/* ═══════════ DISCORD CTA ═══════════ */}
      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <Anim>
            <div className="relative overflow-hidden rounded-3xl p-8 text-center sm:p-16" style={{ background: 'linear-gradient(145deg, rgba(88,101,242,0.18) 0%, rgba(59,68,197,0.06) 50%, rgba(30,30,80,0.04) 100%)', border: '1px solid rgba(88,101,242,0.2)', boxShadow: '0 24px 80px rgba(88,101,242,0.08)' }}>
              <Orb className="bv-float" style={{ width: 220, height: 220, top: '-12%', left: '8%', background: 'rgba(88,101,242,0.08)' }} />
              <Orb className="bv-float-slow" style={{ width: 160, height: 160, bottom: '-8%', right: '8%', background: 'rgba(88,101,242,0.06)' }} />
              <div className="relative">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl" style={{ background: 'rgba(88,101,242,0.15)', border: '1px solid rgba(88,101,242,0.25)', boxShadow: '0 0 30px rgba(88,101,242,0.15)' }}>
                  <DiscordIcon size={28} white />
                </div>
                <h2 className="font-display text-4xl sm:text-5xl tracking-tight mb-4 chrome-text">JOIN THE COMMUNITY</h2>
                <p className="text-base leading-relaxed mb-8 max-w-lg mx-auto" style={{ color: '#8aa0c4' }}>Exclusive deals, early access, live support, and giveaways. Join the PulseDigital Discord server — it's free.</p>
                <a href="https://discord.gg/tGxhDfRkdE" target="_blank" rel="noopener noreferrer" className="btn-discord group rounded-xl px-10 py-4 text-base font-bold hover:scale-105">
                  <DiscordIcon size={18} white className="shrink-0" />
                  Join PulseDigital Discord
                  <svg className="transition-transform duration-200 group-hover:translate-x-1" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </a>
                <div className="mt-5 flex items-center justify-center gap-4 text-xs" style={{ color: '#5a6d8a' }}>
                  <span className="flex items-center gap-1.5"><span className="pulse-dot h-1.5 w-1.5 rounded-full inline-block" style={{ background: '#22c55e' }} />Free to join</span>
                  <span className="flex items-center gap-1.5"><span className="pulse-dot h-1.5 w-1.5 rounded-full inline-block" style={{ background: '#60a5fa', animationDelay: '0.5s' }} />Active community</span>
                  <span className="flex items-center gap-1.5"><span className="pulse-dot h-1.5 w-1.5 rounded-full inline-block" style={{ background: '#a855f7', animationDelay: '1s' }} />Live support</span>
                </div>
              </div>
            </div>
          </Anim>
        </div>
      </section>
    </div>
  )
}

function NewHomePage({ featuredProducts }: { featuredProducts: ReturnType<typeof resolveFeaturedDeals> }) {
  const onHeroMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    const r = el.getBoundingClientRect()
    const x = ((e.clientX - r.left) / r.width) * 100
    const y = ((e.clientY - r.top) / r.height) * 100
    el.style.setProperty('--mx', `${x}%`)
    el.style.setProperty('--my', `${y}%`)
    const ry = (x - 50) * 0.08
    const rx = (50 - y) * 0.08
    el.style.setProperty('--rx', `${rx}deg`)
    el.style.setProperty('--ry', `${ry}deg`)
  }

  const onHeroLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    el.style.setProperty('--mx', `50%`)
    el.style.setProperty('--my', `35%`)
    el.style.setProperty('--rx', `0deg`)
    el.style.setProperty('--ry', `0deg`)
  }

  return (
    <div className="nv-home">
      <section className="nv-hero">
        <div className="nv-wrap">
          <div className="nv-hero3">
            <div className="nv-hero3-spot" aria-hidden />
            <div className="nv-hero3-badges" role="list" aria-label="Trust badges">
              <span className="nv-hero3-badge" role="listitem">
                <svg className="nv-hero3-badge-ic" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M12 2l7 4v6c0 5-3.5 9.5-7 10-3.5-.5-7-5-7-10V6l7-4z"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9.25 12.2l1.9 2.05 3.7-4.1"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Verified checkout
              </span>
              <span className="nv-hero3-badge" role="listitem">
                <svg className="nv-hero3-badge-ic" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M7 11V8a5 5 0 0110 0v3"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6 11h12v10H6V11z"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinejoin="round"
                  />
                </svg>
                SSL encrypted
              </span>
              <span className="nv-hero3-badge" role="listitem">
                <svg className="nv-hero3-badge-ic" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M12 8v4l3 2"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M21 12a9 9 0 11-9-9 9 9 0 019 9z"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinejoin="round"
                  />
                </svg>
                Vouch within 24h of delivery (eligible listings)
              </span>
            </div>
            <div className="nv-hero3-frame" onMouseMove={onHeroMove} onMouseLeave={onHeroLeave}>
              <div className="nv-grain" aria-hidden />
              <div className="nv-hero3-kicker">PulseDigital</div>
              <h1 className="nv-hero3-h">
                <span className="nv-hero3-h-line">A trusted place to buy</span>
                <HeroRotatingPhrase phrases={NV_HERO_PHRASES} />
              </h1>
              <p className="nv-hero3-sub">Verified checkout, clear policies, and support you can reach.</p>

              <div className="nv-hero3-ctas">
                <Link to="/shop" className="nv-btn nv-btn-primary nv-btn-shine">
                  Browse Pulse Shop
                </Link>
                <a
                  href="https://discord.gg/tGxhDfRkdE"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nv-btn nv-btn-discord"
                >
                  Contact support
                </a>
                <Link to="/refund" className="nv-btn nv-btn-ghost">
                  Refund policy
                </Link>
              </div>

              <div className="nv-hero3-trustline">
                Payments processed by <strong>MoneyMotion</strong> · SSL encrypted checkout · Policy links:{' '}
                <Link to="/terms" className="nv-hero3-link">
                  Terms
                </Link>{' '}
                ·{' '}
                <Link to="/refund" className="nv-hero3-link">
                  Refunds
                </Link>
              </div>

              <div className="nv-hero3-micro" role="list" aria-label="Hero proof points">
                <span className="nv-hero3-micro-item" role="listitem">
                  <span className="nv-hero3-micro-dot" aria-hidden /> Verified checkout
                </span>
                <span className="nv-hero3-micro-item" role="listitem">
                  <span className="nv-hero3-micro-dot" aria-hidden /> Policy-backed replacement (eligible)
                </span>
                <span className="nv-hero3-micro-item" role="listitem">
                  <span className="nv-hero3-micro-dot" aria-hidden /> Support available
                </span>
              </div>
            </div>

            <div className="nv-seals" role="list" aria-label="Trust seals">
              <div className="nv-seal" role="listitem">
                <div className="nv-seal-title">Payments</div>
                <div className="nv-seal-sub">MoneyMotion · SSL encrypted</div>
              </div>
              <div className="nv-seal" role="listitem">
                <div className="nv-seal-title">Policies</div>
                <div className="nv-seal-sub">Eligibility rules · replacement windows</div>
              </div>
              <div className="nv-seal" role="listitem">
                <div className="nv-seal-title">Support</div>
                <div className="nv-seal-sub">Discord-first · guided resolution</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="nv-section nv-section-alt">
        <div className="nv-wrap">
          <div className="nv-section-head">
            <h2 className="nv-h2">Featured drops</h2>
            <p className="nv-muted">Hand-picked deals with the best value-to-delivery ratio right now.</p>
          </div>
          <div className="nv-grid">
            {featuredProducts.slice(0, 8).map((p) => (
              <ProductCard key={p.id} product={p} variant="vault" />
            ))}
          </div>
          <div className="nv-center">
            <div className="nv-featured-cta">
              <Link to="/shop" className="nv-btn nv-btn-primary">
                View all products
              </Link>
              <Link to="/shop" search={{ sort: 'new', q: undefined, category: undefined }} className="nv-btn nv-btn-ghost">
                New drops
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="nv-section nv-marquee">
        <div className="nv-wrap">
          <div className="nv-marquee-bar" aria-label="Trust highlights">
            <div className="nv-marquee-track">
              {[...Array(2)].map((_, setIdx) => (
                <span className="nv-marquee-set" key={setIdx} aria-hidden={setIdx === 1}>
                  {[
                    'Verified checkout',
                    'SSL encrypted',
                    'Clear policies',
                    'Replacement windows (eligible)',
                    'Discord-first support',
                    'MoneyMotion processing',
                  ].map((t) => (
                    <span className="nv-marquee-item" key={`${setIdx}-${t}`}>
                      <span className="nv-marquee-dot" aria-hidden />
                      {t}
                    </span>
                  ))}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="nv-section nv-trust">
        <div className="nv-trust-ambient" aria-hidden />
        <div className="nv-wrap">
          <div className="nv-trust-head">
            <p className="nv-trust-kicker">Built for trust</p>
            <h2 className="nv-h2 nv-h2--center">Why PulseDigital</h2>
            <p className="nv-muted nv-muted--center">Legitimate before, during, and after checkout — not just at the hero.</p>
          </div>

          <div className="nv-bento" role="list">
            {NV_WHY_PILLARS.map((item, i) => (
              <Anim key={item.title} delay={i * 0.07} className="nv-bento-card" role="listitem">
                <span className="nv-bento-num" aria-hidden>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div
                  className="nv-bento-icon"
                  style={{
                    background: `${item.color}14`,
                    border: `1px solid ${item.color}30`,
                    color: item.color,
                  }}
                >
                  <FeatureIcon name={item.icon} size={22} strokeWidth={1.75} className="text-current" />
                </div>
                <h3 className="nv-bento-t">{item.title}</h3>
                <p className="nv-bento-s">{item.desc}</p>
                <div
                  className="nv-bento-glow"
                  aria-hidden
                  style={{ background: `radial-gradient(ellipse at 24% 16%, ${item.color}14, transparent 62%)` }}
                />
              </Anim>
            ))}
          </div>

          <div className="nv-statbar" role="list" aria-label="Key stats">
            <div className="nv-statbar-item" role="listitem">
              <div className="nv-statbar-n">SSL</div>
              <div className="nv-statbar-l">Encrypted checkout</div>
            </div>
            <div className="nv-statbar-item" role="listitem">
              <div className="nv-statbar-n">24h</div>
              <div className="nv-statbar-l">Replacement (eligible)</div>
            </div>
            <div className="nv-statbar-item" role="listitem">
              <div className="nv-statbar-n">Discord</div>
              <div className="nv-statbar-l">Support channel</div>
            </div>
          </div>

          <div className="nv-guarantee nv-guarantee--premium">
            <div className="nv-guarantee-l">
              <div className="nv-guarantee-k">Assurance</div>
              <div className="nv-guarantee-h">Policies you can verify before purchase.</div>
              <div className="nv-guarantee-s">Replacement windows apply on eligible listings. Review terms and refunds anytime.</div>
            </div>
            <div className="nv-guarantee-r">
              <Link to="/refund" className="nv-btn nv-btn-primary">
                Refund policy
              </Link>
              <Link to="/terms" className="nv-btn nv-btn-ghost">
                Terms
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="nv-section nv-section-alt">
        <div className="nv-wrap">
          <div className="nv-section-head">
            <h2 className="nv-h2">Social proof</h2>
            <p className="nv-muted">What buyers say when things matter: delivery, support, and outcomes.</p>
          </div>
          <div className="nv-proof-grid" role="list">
            {SOCIAL_PROOF.slice(0, 6).map((t) => (
              <div key={t} className="nv-proof" role="listitem">
                <div className="nv-proof-q">“{t}”</div>
                <div className="nv-proof-s">Verified buyer</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="nv-section">
        <div className="nv-wrap">
          <div className="nv-steps">
            <div className="nv-steps-head">
              <h2 className="nv-h2">How it works</h2>
              <p className="nv-muted">A simple flow with clear expectations.</p>
            </div>
            <div className="nv-steps-grid" role="list">
              <div className="nv-step" role="listitem">
                <div className="nv-step-n">01</div>
                <div className="nv-step-t">Choose a listing</div>
                <div className="nv-step-s">Browse Pulse Shop and pick the product that fits your needs.</div>
              </div>
              <div className="nv-step" role="listitem">
                <div className="nv-step-n">02</div>
                <div className="nv-step-t">Checkout securely</div>
                <div className="nv-step-s">Payments are processed by MoneyMotion. SSL encrypted.</div>
              </div>
              <div className="nv-step" role="listitem">
                <div className="nv-step-n">03</div>
                <div className="nv-step-t">Get support if needed</div>
                <div className="nv-step-s">If something goes wrong, contact support and we’ll guide resolution.</div>
              </div>
            </div>
            <div className="nv-center">
              <div className="nv-featured-cta">
                <Link to="/faq" className="nv-btn nv-btn-ghost">
                  Read FAQ
                </Link>
                <Link to="/contact" className="nv-btn nv-btn-ghost">
                  Contact
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="nv-section nv-section-alt">
        <div className="nv-wrap">
          <div className="nv-policy">
            <div className="nv-policy-head">
              <h2 className="nv-h2">Trust, in writing</h2>
              <p className="nv-muted">Transparent policies you can review before purchase.</p>
            </div>
            <div className="nv-policy-grid">
              <Link to="/terms" className="nv-policy-card">
                <div className="nv-policy-t">Terms</div>
                <div className="nv-policy-s">What’s included, what isn’t, and how we handle issues.</div>
              </Link>
              <Link to="/refund" className="nv-policy-card">
                <div className="nv-policy-t">Refund & replacement</div>
                <div className="nv-policy-s">Eligibility rules and replacement windows by listing.</div>
              </Link>
              <Link to="/contact" className="nv-policy-card">
                <div className="nv-policy-t">Support</div>
                <div className="nv-policy-s">How to reach us and what to include for fastest resolution.</div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="nv-section nv-section-alt">
        <div className="nv-wrap">
          <div className="nv-final-card">
            <div className="nv-final-k">Support</div>
            <div className="nv-final-h">Help keep Pulse Shop online.</div>
            <div className="nv-final-s">
              If PulseDigital has helped you, you can support hosting costs, infrastructure, and new product drops. Every contribution goes back into the project.
            </div>
            <div className="nv-featured-cta">
              <a
                href="https://discord.gg/tGxhDfRkdE"
                target="_blank"
                rel="noopener noreferrer"
                className="nv-btn nv-btn-discord"
              >
                Discuss donation in Discord
              </a>
              <Link to="/contact" className="nv-btn nv-btn-ghost">
                Contact about sponsoring
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="nv-section">
        <div className="nv-wrap">
          <div className="nv-split">
            <div className="nv-split-a">
              <div className="nv-section-head">
                <h2 className="nv-h2">Popular categories</h2>
                <p className="nv-muted">Jump straight to what you’re looking for.</p>
              </div>
              <div className="nv-catgrid" role="list" aria-label="Top categories">
                {shopBrowseCategories.slice(0, 6).map((c) => (
                  <Link
                    key={c.id}
                    to="/shop"
                    search={{ category: c.id, q: undefined, sort: undefined }}
                    className="nv-cat"
                    role="listitem"
                  >
                    <div className="nv-cat-t">{c.label}</div>
                    <div className="nv-cat-s">{c.description}</div>
                    <div className="nv-cat-a">Browse →</div>
                  </Link>
                ))}
              </div>
              <div className="nv-chips">
                {shopBrowseCategories.slice(0, 10).map((c) => (
                  <Link
                    key={c.id}
                    to="/shop"
                    search={{ category: c.id, q: undefined, sort: undefined }}
                    className="nv-chip"
                  >
                    {c.label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="nv-split-b">
              <div className="nv-section-head">
                <h2 className="nv-h2">Quick answers</h2>
                <p className="nv-muted">The most common questions — answered.</p>
              </div>
              <div className="nv-faq-mini">
                {faqs.slice(0, 3).map((f) => (
                  <div key={f.q} className="nv-faq-row">
                    <div className="nv-faq-q">{f.q}</div>
                    <div className="nv-faq-a">{f.a}</div>
                  </div>
                ))}
              </div>
              <div className="nv-center">
                <div className="nv-featured-cta">
                  <Link to="/faq" className="nv-btn nv-btn-primary">
                    Read full FAQ
                  </Link>
                  <Link to="/shop" className="nv-btn nv-btn-ghost">
                    Browse products
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="nv-section nv-final">
        <div className="nv-wrap">
          <div className="nv-final-card">
            <div className="nv-final-k">Ready</div>
            <div className="nv-final-h">Enter Pulse Shop.</div>
            <div className="nv-final-s">Browse curated listings with policies and support you can trust.</div>
            <div className="nv-featured-cta">
              <Link to="/shop" className="nv-btn nv-btn-primary">
                Browse Pulse Shop
              </Link>
              <Link to="/contact" className="nv-btn nv-btn-ghost">
                Contact
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
